import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import api from '../api/axios';
import { AuthContext } from '../context/AuthContext';
import { mapplsAutosuggest, mapplsReverseGeocode, getCoordinatesFromELoc } from '../utils/geocoding';

const AddAddress = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [addresses, setAddresses] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    address: '',
    address_type: 'home',
    lat: null,
    lng: null,
  });
  const [editingAddressId, setEditingAddressId] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // API Functions
  const fetchAddresses = async () => {
    try {
      setIsLoading(true);
      const res = await api.get('/addresses/', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAddresses(res.data.addresses || res.data.data || []);
    } catch (error) {
      console.error('Error fetching addresses:', error);
      toast.error('Failed to load addresses. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const addAddress = async (addressData) => {
    try {
      setIsSubmitting(true);
      const res = await api.post('/addresses/add/', addressData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAddresses(prev => [...prev, res.data.address || res.data]);
      toast.success('Address added successfully');
      return true;
    } catch (error) {
      console.error('Error adding address:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to add address. Please try again.';
      toast.error(errorMessage);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateAddress = async (addressId, addressData) => {
    try {
      setIsSubmitting(true);
      const res = await api.put(`/addresses/${addressId}/edit/`, addressData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAddresses(prev => prev.map(addr =>
        addr.id === addressId ? (res.data.address || res.data) : addr
      ));
      toast.success('Address updated successfully');
      return true;
    } catch (error) {
      console.error('Error updating address:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to update address. Please try again.';
      toast.error(errorMessage);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteAddress = async (addressId) => {
    try {
      await api.delete(`/addresses/${addressId}/delete/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAddresses(prev => prev.filter(addr => addr.id !== addressId));
      toast.success('Address deleted successfully');
      return true;
    } catch (error) {
      console.error('Error deleting address:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to delete address. Please try again.';
      toast.error(errorMessage);
      return false;
    }
  };

  // Load addresses on component mount
  useEffect(() => {
    if (token) {
      fetchAddresses();
    }
  }, [token]);


  // Mappls Autocomplete address search
  useEffect(() => {
    if (!formData.address || formData.address.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const places = await mapplsAutosuggest(formData.address);
        setSuggestions(places);
        setShowSuggestions(places.length > 0);
      } catch {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [formData.address]);

  const getCurrentLocation = async () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by this browser');
      return;
    }

    setIsGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          const address = await mapplsReverseGeocode(latitude, longitude);
          setFormData((prev) => ({ ...prev, address, lat: latitude, lng: longitude }));
          toast.success('Current location detected successfully');
        } catch {
          setFormData((prev) => ({ ...prev, lat: latitude, lng: longitude }));
          toast.success('Location coordinates obtained');
        } finally {
          setIsGettingLocation(false);
        }
      },
      (error) => {
        setIsGettingLocation(false);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            toast.error('Please allow location access to use this feature');
            break;
          case error.POSITION_UNAVAILABLE:
            toast.error('Location information is unavailable');
            break;
          case error.TIMEOUT:
            toast.error('Location request timed out. Please try again');
            break;
          default:
            toast.error('Unable to retrieve location. Please try again');
            break;
        }
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
    );
  };

  const handleSuggestionClick = (suggestion) => {
    const address = suggestion.placeAddress
      ? `${suggestion.placeName}, ${suggestion.placeAddress}`
      : suggestion.placeName;
    const coords = getCoordinatesFromELoc(suggestion);
    setFormData((prev) => ({ ...prev, address, lat: coords?.lat || null, lng: coords?.lng || null }));
    setSuggestions([]);
    setShowSuggestions(false);
    toast.success('Location selected');
  };

  const handleSubmit = async () => {
    const { name, mobile, address, lat, lng } = formData;
    if (!name.trim() || !mobile.trim() || !address.trim() || !lat || !lng) {
      toast.error('Please fill all fields and select a location');
      return;
    }

    if (!/^[6-9]\d{9}$/.test(mobile.trim())) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }

    const addressData = {
      name: name.trim(),
      phone: mobile.trim(),
      address: address.trim(),
      address_type: formData.address_type || 'home',
      latitude: lat,
      longitude: lng,
    };

    let success = false;
    if (editingAddressId !== null) {
      success = await updateAddress(editingAddressId, addressData);
    } else {
      success = await addAddress(addressData);
    }

    if (success) {
      resetForm();
    }
  };

  const handleDelete = async (addressId) => {
    if (window.confirm('Are you sure you want to delete this address?')) {
      await deleteAddress(addressId);
    }
  };

  const handleEdit = (address) => {
    setFormData({
      name: address.name,
      mobile: address.phone,
      address: address.address,
      address_type: address.address_type || 'home',
      lat: address.latitude,
      lng: address.longitude,
    });
    setEditingAddressId(address.id);
  };

  const resetForm = () => {
    setFormData({ name: '', mobile: '', address: '', address_type: 'home', lat: null, lng: null });
    setEditingAddressId(null);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  // Check if user is authenticated
  if (!token) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Authentication Required</h3>
            <p className="text-gray-600">Please log in to access your addresses</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Header />
      <ToastContainer 
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          {/* Back Button */}
          <button
            onClick={() => navigate('/profile')}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-800 font-semibold transition-colors mb-6"
          >
            <ArrowLeft size={20} />
            Back to Profile
          </button>

          {/* Header */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Address Management</h1>
                <p className="text-gray-600 mt-1">Add and manage your delivery addresses</p>
              </div>
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {addresses.length} saved addresses
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Form Section */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sticky top-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {editingAddressId !== null ? 'Edit Address' : 'Add New Address'}
                  </h2>
                  {editingAddressId !== null && (
                    <button
                      onClick={resetForm}
                      className="text-sm text-gray-500 hover:text-gray-700 flex items-center"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      Cancel
                    </button>
                  )}
                </div>

                <div className="space-y-4">
                  {/* Name Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name
                    </label>
                    <input
                      type="text"
                      placeholder="Enter your full name"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      value={formData.name}
                      onChange={(e) => setFormData((f) => ({ ...f, name: e.target.value }))}
                    />
                  </div>

                  {/* Mobile Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mobile Number
                    </label>
                    <input
                      type="tel"
                      placeholder="Enter 10-digit mobile number"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      value={formData.mobile}
                      onChange={(e) => setFormData((f) => ({ ...f, mobile: e.target.value }))}
                    />
                  </div>

                  {/* Address Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Address
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Type your full address"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                        value={formData.address}
                        onChange={(e) => setFormData((f) => ({ ...f, address: e.target.value }))}
                        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                      />
                      <svg className="absolute right-3 top-3.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>

                      {showSuggestions && suggestions.length > 0 && (
                        <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {suggestions.map((suggestion) => (
                            <div
                              key={suggestion.place_id}
                              className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors duration-150"
                              onClick={() => handleSuggestionClick(suggestion)}
                            >
                              <div className="flex items-start space-x-3">
                                <svg className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                </svg>
                                <div>
                                  <div className="text-sm font-medium text-gray-900">{suggestion.placeName}</div>
                                  <div className="text-xs text-gray-500">{suggestion.placeAddress}</div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Location Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Select Location
                    </label>
                    <div className="grid grid-cols-1 gap-3">
                      <button
                        className="flex items-center justify-center px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={getCurrentLocation}
                        disabled={isGettingLocation}
                      >
                        {isGettingLocation ? (
                          <>
                            <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                            Detecting Location...
                          </>
                        ) : (
                          <>
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            </svg>
                            Use Current Location
                          </>
                        )}
                      </button>
                      

                    </div>
                  </div>

                  {/* Address Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Address Type
                    </label>
                    <select
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      value={formData.address_type}
                      onChange={(e) => setFormData((f) => ({ ...f, address_type: e.target.value }))}
                    >
                      <option value="home">Home</option>
                      <option value="office">Office</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  {/* Selected Location Display */}
                  {formData.lat && formData.lng && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center space-x-2">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-sm font-medium text-blue-900">Location Selected</span>
                      </div>
                      <div className="text-xs text-blue-700 mt-1">
                        Coordinates: {formData.lat.toFixed(6)}, {formData.lng.toFixed(6)}
                      </div>
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-white py-3 rounded-lg font-medium transition-all duration-200 flex items-center justify-center"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    {isSubmitting 
                      ? (editingAddressId !== null ? 'Updating...' : 'Adding...') 
                      : (editingAddressId !== null ? 'Update Address' : 'Add Address')
                    }
                  </button>
                </div>
              </div>
            </div>

            {/* Addresses List Section */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="p-6 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Saved Addresses</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {addresses.length === 0 ? 'No addresses saved yet' : `${addresses.length} address${addresses.length > 1 ? 'es' : ''} saved`}
                  </p>
                </div>
                
                <div className="p-6">
                  {isLoading ? (
                    <div className="text-center py-12">
                      <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                      <p className="text-gray-600">Loading addresses...</p>
                    </div>
                  ) : addresses.length === 0 ? (
                    <div className="text-center py-12">
                      <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No addresses yet</h3>
                      <p className="text-gray-600">Add your first address to get started with deliveries</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {addresses.map((addr) => (
                        <div 
                          key={addr.id} 
                          className={`border rounded-lg p-4 transition-all duration-200 hover:shadow-md ${
                            editingAddressId === addr.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white'
                          }`}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-2">
                                <h4 className="font-semibold text-gray-900">{addr.name}</h4>
                                <span className="text-sm text-gray-500">•</span>
                                <span className="text-sm text-gray-600">{addr.phone}</span>
                              </div>
                              <p className="text-gray-700 mb-2 leading-relaxed">{addr.address}</p>
                              <div className="flex items-center text-xs text-gray-500 space-x-4">
                                <span>Lat: {addr.latitude?.toFixed(4) || 'N/A'}</span>
                                <span>Lng: {addr.longitude?.toFixed(4) || 'N/A'}</span>
                              </div>
                            </div>
                            
                            <div className="flex space-x-2 ml-4">
                              <button
                                className="px-3 py-1.5 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 border border-blue-300 rounded-md transition-all duration-200"
                                onClick={() => handleEdit(addr)}
                              >
                                Edit
                              </button>
                              <button
                                className="px-3 py-1.5 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 border border-red-300 rounded-md transition-all duration-200"
                                onClick={() => handleDelete(addr.id)}
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default AddAddress;